import type { PatternPreset } from '~/types/presets';
import type { DevicePattern } from '~/types/midi';
import musicData from '~/data/musicData';
import { PATTERNS, type PatternParts } from '~/types/pattern';

/**
 * MIDI Hardware Default Pattern
 *
 * A groovy, syncopated pattern in G# Major Pentatonic with eighth note phrasing.
 * Designed to work well with a wide variety of hardware synthesizers.
 *
 * Musical characteristics:
 * - Key: G# Major Pentatonic (G#, A#, C, D#, F)
 * - Structure: ABCB (4-part form with recurring B section)
 * - Note Length: Eighth notes (8n)
 * - Density: Moderate (35% space probability)
 * - Range: 1 octave (12 semitones) for focused melodic movement
 * - Dissonance: 4% (occasional chromatic passing tones)
 *
 * Pattern parts:
 * - A: Rhythmic groove with G#2/A#2 emphasis
 * - B: Sparse syncopated phrase with C2/D#2
 * - C: Ascending phrase (F2 → G#2) with rests
 */

const defaultHardwarePattern: DevicePattern = {
  rootNote: musicData.rootNotes.find(n => n.note === 'G♯') || musicData.rootNotes[0],
  scale: musicData.scales.find(s => s.id === 'pentatonic-maj') || musicData.scales[0],
  scaleMode: 'Major',
  pattern: PATTERNS.find(p => p.name === 'ABCB') || PATTERNS[1],
  patternParts: {
    A: [
      { note: 'G#2', duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'A#2', duration: '8n' },
      { note: 'A#2', duration: '8n' },
    ],
    B: [
      { note: null, duration: '8n' },
      { note: 'C2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
    ],
    C: [
      { note: 'F2', duration: '8n' },
      { note: 'F2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'G#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: null, duration: '8n' },
    ],
    D: [
      { note: 'A#2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'C2', duration: '8n' },
      { note: 'C2', duration: '8n' },
      { note: null, duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: 'D#2', duration: '8n' },
      { note: null, duration: '8n' },
    ],
  } as PatternParts,
  beatsPerBar: 4,
  tempo: 110,
  swing: 0,
  space: 35,
  spaceMin: 20,
  spaceMax: 50,
  dissonance: 2,
  dissonanceMin: 0,
  dissonanceMax: 2,
  spread: 7,
  spreadMin: 0,
  spreadMax: 24,
  range: 12,
  rangeMin: 12,
  rangeMax: 37,
  scrambleToggles: {
    scrambleStructure: true,
    scrambleRootNote: true,
    scrambleMode: true,
    scrambleScale: true,
    scrambleSpace: true,
    scrambleDissonance: true,
    scrambleSpread: true,
    scrambleRange: true,
  },
};

/**
 * MIDI Hardware Default Pattern Preset
 */
export const DEFAULT_HARDWARE_PATTERN_PRESET: PatternPreset = {
  id: 'default-hardware-pattern',
  name: 'Default',
  pattern: defaultHardwarePattern,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the MIDI hardware default pattern preset
 * Returns a deep copy to avoid mutation
 */
export function getDefaultHardwarePattern(): PatternPreset {
  return {
    ...DEFAULT_HARDWARE_PATTERN_PRESET,
    pattern: {
      ...DEFAULT_HARDWARE_PATTERN_PRESET.pattern,
      rootNote: { ...DEFAULT_HARDWARE_PATTERN_PRESET.pattern.rootNote },
      scale: { ...DEFAULT_HARDWARE_PATTERN_PRESET.pattern.scale },
      pattern: { ...DEFAULT_HARDWARE_PATTERN_PRESET.pattern.pattern },
      patternParts: JSON.parse(JSON.stringify(DEFAULT_HARDWARE_PATTERN_PRESET.pattern.patternParts)),
      scrambleToggles: { ...DEFAULT_HARDWARE_PATTERN_PRESET.pattern.scrambleToggles },
    },
  };
}
